/**
 * Top shell for studio-style surfaces (batch tools today; same header can power
 * a unified home / tool hub later with different `title` / `subtitle` or brand
 * slot, e.g. Comic, Mascot, or Cartwheel + animated mark).
 */
import { Link } from 'react-router';

import Text from '@cartwhl/ui/base/Text';
import HomeIcon from '@cartwhl/ui/icons/HomeIcon';
import Button from '@cartwhl/ui/input/Button';
import UserMenuWithCredits from '@cartwhl/ui/organisms/UserMenuWithCredits';

export type StudioShellHeaderProps = {
  /** Product or surface name, e.g. "Comic", "Motion Generation", "Mascot", "Cartwheel". */
  title: string;
  /** One-line gray descriptor next to the title; omit when empty. */
  subtitle?: string;
  /**
   * When set, home navigates via anchor (e.g. cross-app dashboard URL).
   * Otherwise uses in-app react-router `/`.
   */
  homeHref?: string;
  /** Accessible label for the home control. */
  homeAriaLabel?: string;
};

export default function StudioShellHeader({
  title,
  subtitle,
  homeHref,
  homeAriaLabel = 'All applets',
}: StudioShellHeaderProps) {
  /** Original shell was px-4 / sm:px-6; ×2 from that baseline. */
  const shellPadX = 'px-8 sm:px-12';

  /** Tweak header title vs. blurb size here (Tailwind arbitrary `text-[…]` / `leading-[…]`). */
  const shellTitleSize = 'text-[25px]! leading-[29px]!';
  const shellSubtitleSize = 'text-[24px]! leading-[28px]!';

  const homeButton = (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      icon={<HomeIcon />}
      tabIndex={-1}
      aria-hidden
    />
  );

  return (
    <header
      className={`bg-primary border-gray-30 dark:border-gray-75 z-50 flex w-full shrink-0 items-center justify-between border-b py-3 ${shellPadX}`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-4 sm:gap-5">
        {homeHref ? (
          <a href={homeHref} aria-label={homeAriaLabel}>
            {homeButton}
          </a>
        ) : (
          <Link to="/" aria-label={homeAriaLabel}>
            {homeButton}
          </Link>
        )}
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-4 gap-y-0.5 sm:gap-x-6">
          <Text
            as="h1"
            variant="studio/display/xl"
            className={`text-primary shrink-0 truncate ${shellTitleSize}`}
          >
            {title}
          </Text>
          {subtitle ? (
            <Text
              as="p"
              variant="studio/subtitle"
              className={`text-tertiary min-w-0 truncate ${shellSubtitleSize}`}
            >
              {subtitle}
            </Text>
          ) : null}
        </div>
      </div>

      <UserMenuWithCredits />
    </header>
  );
}
